import React, { useState, useEffect } from 'react';
import './Reservation.css';

import { Heading, PageTitle } from '../../../components';

import { FaUser, FaEnvelope, FaPhoneAlt, FaCalendar, FaClock, FaUsers, FaComment, FaFacebookF, FaTwitter, FaInstagram, FaLinkedin } from 'react-icons/fa';
import axios from 'axios';
import { contactContent } from '../../../constants';


// API endpoint for submitting the booking form
const API_PATH = 'https://chai-tea-react.asdesignsgalaxy.com/api/phpmailer/reservation.php';

// Regex patterns for email and phone number validation
const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;


const Reservation = () => {  

  // Find the relevant data from contacts array
  const hoursData = contactContent.find(item => item.title === 'opening hours');
  const addressData = contactContent.find(item => item.title === 'address');
  const phoneData = contactContent.find(item => item.title === 'call us');


    // State to manage form data
      const [formData, setFormData] = useState({
        name: '',
        email: '',
        number: '',
        guests: '',
        date: '',
        time: '',
        comment: ''
      });
    
      const [mailSent, setMailSent] = useState(false);
      const [error, setError] = useState('');
      const [isSubmitting, setIsSubmitting] = useState(false);
    
      useEffect(() => {
        if (mailSent) {
          const timer = setTimeout(() => setMailSent(false), 3000);
          return () => clearTimeout(timer);
        }
      }, [mailSent]);
    
      const handleSubmitForm = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');
    
        try {
          const validationErrors = [];
    
          if (!formData.name.trim()) validationErrors.push('Name is required');
          if (!formData.email.trim()) {
            validationErrors.push('Email is required');
          } else if (!EMAIL_REGEX.test(formData.email)) {
            validationErrors.push('Invalid email format');
          }
          if (!formData.number.trim()) {
            validationErrors.push('Phone number is required');
          } else if (!PHONE_REGEX.test(formData.number)) {
            validationErrors.push('Invalid phone number format');
          }
          if (!formData.guests) validationErrors.push('Number of guests is required');
          if (!formData.date.trim()) validationErrors.push('Date is required');
          if (!formData.time.trim()) validationErrors.push('Time is required');
          if (!formData.comment.trim()) validationErrors.push('Comment is required');
    
          if (validationErrors.length > 0) {
            throw new Error(validationErrors.join(' | '));
          }
    
          const postData = new FormData();
          Object.entries(formData).forEach(([key, value]) => {
            postData.append(key, value);
          });
    
          const response = await axios.post(API_PATH, postData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
    
          if (!response.data.sent) {
            throw new Error(response.data.message || 'Reservation submission failed');
          }
    
          setMailSent(true);
          setFormData({
            name: '',
            email: '',
            number: '',
            guests: '',
            date: '',
            time: '',
            comment: ''
          });
        } catch (err) {
          setError(err.response?.data?.message || err.message || 'An error occurred');
        } finally {
          setIsSubmitting(false);
        }
      };
    
      const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
      };


  return (
    <>
      <PageTitle title={'reservation'} page={'reservation'} /> 
      <section className='reservation'>

        <div className="box-container">

          {/* Booking Information Section */}
          <div className="booking-info">

            {/* Working Hours Block */}
            <div className="item">
              <h3>Working Hours</h3>
              {hoursData?.content.map((time, index) => {
                // Split each string into day range and timing (e.g., "Mon - Fri: 8 AM - 6 PM")
                const [days, hours] = time.split(": ");
                return (
                  <div className="timing" key={index}>
                    <h4>{days}</h4>
                    <p>{hours}</p>
                  </div>
                );
              })}
            </div>

            {/* Restaurant Address Block */}
            <div className="item">
              <h3>Restaurant Address</h3>
              <div className="address">
                {/* Display the first address from content array */}
                <p>{addressData?.content[0]}</p>
              </div>
            </div>

            {/* Contact Info Block */}
            <div className="contact">
              {/* Display primary phone number */}
              <p>Call us: {phoneData?.content[0]}</p>

              {/* Social Media Icons */}
              <p>
                Follow us:
                <span className="social">
                  {/* Each icon has an accessible label and link (replace # with actual links) */}
                  <a href="/#" aria-label="Facebook"><FaFacebookF className="icon" /></a>
                  <a href="/#" aria-label="Twitter"><FaTwitter className="icon" /></a>
                  <a href="/#" aria-label="Instagram"><FaInstagram className="icon" /></a>
                  <a href="/#" aria-label="LinkedIn"><FaLinkedin className="icon" /></a>
                </span>
              </p>
            </div>

          </div>

          {/* Reservation Form */}
          <form className='custom-form' onSubmit={handleSubmitForm}>

            {/* Form Heading */}
            <Heading title={"Table Reservation"} sideHeading />

            <div className='input-box'>

              {/* Name Input Field */}
              <div className="input-field">
                <label htmlFor="name"><FaUser /></label>
                <input type="text" name="name" className="box" id="name" 
                  value={formData.name} onChange={handleInputChange}
                  placeholder="Name" disabled={isSubmitting} required />
              </div>

              {/* Email Input Field */}
              <div className="input-field">
                <label htmlFor="email"><FaEnvelope /></label>
                <input type="email" name="email" className="box" id="email" 
                  value={formData.email} onChange={handleInputChange}
                  placeholder="Email" disabled={isSubmitting} required />
              </div>

            </div>

            <div className='input-box'>

              {/* Number Input Field */}
              <div className="input-field">
                <label htmlFor="number"><FaPhoneAlt /></label>
                <input type="number" name="number" className="box" id="number" 
                value={formData.number} onChange={handleInputChange}
                placeholder="Phone Number" disabled={isSubmitting} required />
              </div>

              {/* Number of Guests Input Field */}
              <div className="input-field">
                <label htmlFor="guests"><FaUsers /></label>
                <input type="tel" name="guests" className="box" id="guests"
                    value={formData.guests} onChange={handleInputChange}
                    min="1" max="100" placeholder="Number Of Guests" disabled={isSubmitting} required />
              </div>

            </div>

            <div className='input-box'>

              {/* Date Input Field */}
              <div className="input-field">
                <label htmlFor="date"><FaCalendar /></label>
                <input type="text" name="date" placeholder="Date" id="date" className="box" 
                  value={formData.date} onChange={handleInputChange}
                  onFocus={(e) => (e.target.type = 'date')}
                  onBlur={(e) => (e.target.type = 'text')} disabled={isSubmitting} required />
              </div>

              {/* Time Input Field */}
              <div className="input-field">
                <label htmlFor="time"><FaClock /></label>
                <input type="text" name="time" placeholder="Time" id="time" className="box" 
                  value={formData.time} onChange={handleInputChange}
                  onFocus={(e) => (e.target.type = 'time')}
                  onBlur={(e) => (e.target.type = 'text')} disabled={isSubmitting} required />
              </div>

            </div>

            {/* Comment Input Field */}
            <div className='input-box'>
              <div className="input-field text-area">
                <label htmlFor="comment"><FaComment /></label>
                <textarea cols="30" rows="10" name="comment" className="box" id="comment" 
                value={formData.comment} onChange={handleInputChange}
                placeholder="Message" disabled={isSubmitting} required></textarea>
              </div>
            </div>

            {/* Submit Button */}
            <button type="submit" className='btn' disabled={isSubmitting}>
              {isSubmitting ? 'Booking...' : 'Book Table'}
            </button>

            {/* Error and Success Messages */}
            {error && <div className="alert error">{error.split(' | ').map((msg, i) => (<span key={i}>{msg}</span>))}</div>}
            {mailSent && <div className="alert success"><span>Table booked successfully!</span></div>}

          </form>

        </div>

      </section> 
    </>

  )
}

export default Reservation;